import { useState, useMemo, useEffect, useRef } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Sun, Plus, Trash2, Lock, Loader2 } from "lucide-react";
import SiteLayoutMap from "@/components/SiteLayoutMap";
import { generateLiveLayout } from "@/utils/generateLiveLayout";

// ── Constants (mirrors NewProject.jsx) ──────────────────────────────────────

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Chandigarh", "Puducherry",
];

const CHARGER_OPTIONS = [
  { id: "30kW_DC",  label: "30 kW DC Fast",       power: 30,  cost_range: "₹6-9L"     },
  { id: "60kW_DC",  label: "60 kW DC Fast",       power: 60,  cost_range: "₹12-18L"   },
  { id: "120kW_DC", label: "120 kW DC Fast",      power: 120, cost_range: "₹20-28L"   },
  { id: "150kW_DC", label: "150 kW Supercharger", power: 150, cost_range: "₹24-32L"   },
  { id: "22kW_AC",  label: "22 kW AC Slow",       power: 22,  cost_range: "₹0.8-1.2L" },
];

const CHARGER_TYPE_COLORS = {
  "22kW_AC":  "#16a34a",
  "30kW_DC":  "#0ea5e9",
  "60kW_DC":  "#2563eb",
  "120kW_DC": "#4f46e5",
  "150kW_DC": "#7c3aed",
};

const UNIT_FACTORS = { meters: 1, feet: 0.3048, yards: 0.9144 };
const toMeters = (v, unit) => parseFloat(v) * (UNIT_FACTORS[unit] || 1);

const FEATURE_OPTIONS = [
  { id: "cafe",      label: "Café / Restaurant" },
  { id: "store",     label: "Convenience Store" },
  { id: "car_wash",  label: "Car Wash"           },
  { id: "restrooms", label: "Restrooms"          },
];

/** Merge canvas drag positions back into a full layout object */
function buildFrozenLayout(layout, positions) {
  if (!layout || !positions) return layout;
  const origSolarArea =
    layout.solar?.area_sqm ||
    (layout.solar?.position?.width ?? 0) * (layout.solar?.position?.height ?? 0);
  const newSolarArea = positions.solar
    ? positions.solar.width * positions.solar.height
    : origSolarArea;
  const newKwp =
    positions.solar && layout.solar?.estimated_kWp && origSolarArea > 0
      ? parseFloat(((layout.solar.estimated_kWp / origSolarArea) * newSolarArea).toFixed(1))
      : layout.solar?.estimated_kWp;
  return {
    ...layout,
    charger_arrangement: { ...layout.charger_arrangement, positions: positions.chargers },
    parking:             { ...layout.parking, count: positions.parking.length, positions: positions.parking },
    buildings:           positions.buildings,
    solar: layout.solar
      ? { ...layout.solar, position: positions.solar || layout.solar.position, area_sqm: parseFloat(newSolarArea.toFixed(1)), estimated_kWp: newKwp }
      : layout.solar,
  };
}

/** Build initial form state from a SiteInputs object */
function initFormData(si = {}) {
  return {
    land_length:    si.original_length != null ? String(si.original_length) : String(si.land_length  || ""),
    land_width:     si.original_width  != null ? String(si.original_width)  : String(si.land_width   || ""),
    land_unit:      si.land_unit      || "meters",
    land_shape:     si.land_shape     || "rectangular",
    access_points:  String(si.access_points || "1"),
    location_type:  si.location_type  || "urban",
    state:          si.state          || "",
    pin_code:       si.pin_code       || "",
    features:       si.features       || [],
    charger_config: si.charger_config?.length ? si.charger_config : [{ type: "60kW_DC", quantity: 4 }],
    solar_required: si.solar_required !== false,
    budget_range:   si.budget_range   || "",
    land_lease_cost_monthly: si.land_lease_cost_monthly != null ? String(si.land_lease_cost_monthly) : "",
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {boolean}  props.open
 * @param {function} props.onOpenChange
 * @param {object}   props.project        — full project document
 * @param {string}   props.projectId
 * @param {function} props.onSuccess      — called after successful save
 */
export function EditProjectSheet({ open, onOpenChange, project, projectId, onSuccess }) {
  const [formData, setFormData] = useState(() => initFormData(project?.site_inputs));
  const [saving, setSaving] = useState(false);
  const [canvasKey, setCanvasKey] = useState(0);
  const frozenPositionsRef = useRef(null);

  // Re-initialise form from latest project.site_inputs whenever the dialog opens
  useEffect(() => {
    if (!open) return;
    setFormData(initFormData(project?.site_inputs));
    frozenPositionsRef.current = null;
    setCanvasKey(k => k + 1);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (field, value) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const toggleFeature = feature =>
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature],
    }));

  const addChargerConfig = () => {
    const usedTypes = formData.charger_config.map(c => c.type);
    const available = CHARGER_OPTIONS.find(o => !usedTypes.includes(o.id));
    if (available)
      setFormData(prev => ({ ...prev, charger_config: [...prev.charger_config, { type: available.id, quantity: 2 }] }));
  };

  const removeChargerConfig = index => {
    if (formData.charger_config.length > 1)
      setFormData(prev => ({ ...prev, charger_config: prev.charger_config.filter((_, i) => i !== index) }));
  };

  const updateChargerConfig = (index, field, value) =>
    setFormData(prev => ({
      ...prev,
      charger_config: prev.charger_config.map((c, i) =>
        i === index
          ? { ...c, [field]: field === "quantity" ? parseInt(value) || 1 : value }
          : c
      ),
    }));

  const lengthInMeters = useMemo(() => toMeters(formData.land_length, formData.land_unit), [formData.land_length, formData.land_unit]);
  const widthInMeters  = useMemo(() => toMeters(formData.land_width,  formData.land_unit), [formData.land_width,  formData.land_unit]);

  const solarFeasible = useMemo(
    () => !lengthInMeters || !widthInMeters || (lengthInMeters >= 10 && widthInMeters >= 10),
    [lengthInMeters, widthInMeters],
  );

  const maxFittingChargers = useMemo(() => {
    if (!lengthInMeters || !widthInMeters) return 4;
    const usableL = lengthInMeters - 6;
    const usableW = widthInMeters  - 6;
    if (usableL < 7.4 || usableW < 6) return 1;
    const perRow = Math.max(1, Math.floor(usableL / 8.9));
    return Math.max(1, perRow * (usableW > 20 ? 2 : 1));
  }, [lengthInMeters, widthInMeters]);

  const getTotalChargers = () => formData.charger_config.reduce((s, c) => s + (c.quantity || 0), 0);
  const getTotalPower    = () => formData.charger_config.reduce((s, c) => {
    const info = CHARGER_OPTIONS.find(o => o.id === c.type);
    return s + (info?.power || 0) * (c.quantity || 0);
  }, 0);

  const liveLayout = useMemo(() => {
    if (!lengthInMeters || !widthInMeters) return null;
    return generateLiveLayout({ ...formData, land_length: lengthInMeters, land_width: widthInMeters });
  }, [lengthInMeters, widthInMeters, formData.charger_config, formData.features, formData.solar_required, formData.location_type]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!lengthInMeters || !widthInMeters) {
      toast.error("Please enter land dimensions");
      return;
    }
    if (getTotalChargers() === 0) {
      toast.error("Please add at least one charger");
      return;
    }
    const initial_layout = buildFrozenLayout(liveLayout, frozenPositionsRef.current);
    setSaving(true);
    try {
      const resp = await fetch(`${API}/projects/${projectId}/redesign`, {
        method:      "PUT",
        headers:     { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          initial_layout,
          site_inputs: {
            land_length:     lengthInMeters,
            land_width:      widthInMeters,
            land_unit:       formData.land_unit,
            original_length: parseFloat(formData.land_length),
            original_width:  parseFloat(formData.land_width),
            land_shape:      formData.land_shape,
            access_points:   parseInt(formData.access_points),
            location_type:   formData.location_type,
            state:           formData.state    || null,
            pin_code:        formData.pin_code || null,
            features:        formData.features,
            charger_config:  formData.charger_config,
            total_chargers:  getTotalChargers(),
            total_power_kw:  getTotalPower(),
            solar_required:  formData.solar_required,
            budget_range:    formData.budget_range || null,
            land_lease_cost_monthly: formData.land_lease_cost_monthly ? parseFloat(formData.land_lease_cost_monthly) : null,
          },
        }),
      });
      if (resp.ok) {
        toast.success("Site & design updated — ready for re-analysis.");
        onOpenChange(false);
        onSuccess?.();
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || "Failed to update project");
      }
    } catch {
      toast.error("Failed to update project");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[92vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 py-4 border-b border-slate-700 flex-shrink-0">
          <DialogTitle className="text-lg font-semibold">Edit Site &amp; Design</DialogTitle>
          <p className="text-sm text-slate-500 mt-0.5">
            Modify site details and layout canvas · Saving will reset analysis results to draft
          </p>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden">
          {/* ── Left: scrollable form panel ── */}
          <div className="w-80 flex-shrink-0 flex flex-col border-r border-slate-700 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

              {/* Dimensions */}
              <section className="space-y-2.5">
                <Label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Dimensions</Label>
                <Select value={formData.land_unit} onValueChange={v => handleInputChange("land_unit", v)}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="meters">Meters (m)</SelectItem>
                    <SelectItem value="feet">Feet (ft)</SelectItem>
                    <SelectItem value="yards">Yards (yd)</SelectItem>
                  </SelectContent>
                </Select>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Length ({formData.land_unit})</Label>
                    <Input type="number" value={formData.land_length} onChange={e => handleInputChange("land_length", e.target.value)} className="h-8 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Width ({formData.land_unit})</Label>
                    <Input type="number" value={formData.land_width} onChange={e => handleInputChange("land_width", e.target.value)} className="h-8 text-sm" />
                  </div>
                </div>
              </section>

              {/* Location */}
              <section className="space-y-2.5">
                <Label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Location</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">PIN Code</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="e.g. 400001"
                      value={formData.pin_code}
                      onChange={e => handleInputChange("pin_code", e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">State</Label>
                    <Select value={formData.state} onValueChange={v => handleInputChange("state", v)}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {INDIAN_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Location Type</Label>
                    <Select value={formData.location_type} onValueChange={v => handleInputChange("location_type", v)}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urban">Urban</SelectItem>
                        <SelectItem value="highway">Highway</SelectItem>
                        <SelectItem value="semi-urban">Semi-Urban</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Land Shape</Label>
                    <Select value={formData.land_shape} onValueChange={v => handleInputChange("land_shape", v)}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rectangular">Rectangular</SelectItem>
                        <SelectItem value="L-shaped">L-Shaped</SelectItem>
                        <SelectItem value="triangular">Triangular</SelectItem>
                        <SelectItem value="irregular">Irregular</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Access Points</Label>
                  <Select value={formData.access_points} onValueChange={v => handleInputChange("access_points", v)}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Access Point</SelectItem>
                      <SelectItem value="2">2 Access Points</SelectItem>
                      <SelectItem value="3">3+ Access Points</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </section>

              {/* Amenities */}
              <section className="space-y-2">
                <Label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Amenities</Label>
                <div className="grid grid-cols-2 gap-2">
                  {FEATURE_OPTIONS.map(f => (
                    <div key={f.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`edit-${f.id}`}
                        checked={formData.features.includes(f.id)}
                        onCheckedChange={() => toggleFeature(f.id)}
                      />
                      <Label htmlFor={`edit-${f.id}`} className="text-xs font-normal cursor-pointer">{f.label}</Label>
                    </div>
                  ))}
                </div>
              </section>

              {/* Chargers */}
              <section className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    Chargers — {getTotalChargers()} / {getTotalPower()} kW
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-primary px-1.5"
                    onClick={addChargerConfig}
                    disabled={formData.charger_config.length >= CHARGER_OPTIONS.length}
                  >
                    <Plus className="w-3 h-3 mr-0.5" /> Add
                  </Button>
                </div>
                {lengthInMeters > 0 && (
                  <p className="text-[11px] text-slate-400">
                    Site fits up to {maxFittingChargers} charger{maxFittingChargers !== 1 ? "s" : ""}
                  </p>
                )}
                <div className="space-y-1">
                  {formData.charger_config.map((config, index) => {
                    const info     = CHARGER_OPTIONS.find(o => o.id === config.type);
                    const dotColor = CHARGER_TYPE_COLORS[config.type] || "#2563eb";
                    return (
                      <div key={index} className="flex items-center gap-2 py-1.5 px-2 rounded-lg bg-slate-900/50 border border-slate-800">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: dotColor }} />
                        <div className="flex-1 min-w-0">
                          <Select value={config.type} onValueChange={v => updateChargerConfig(index, "type", v)}>
                            <SelectTrigger className="border-0 shadow-none p-0 h-auto text-xs font-medium text-slate-100 focus:ring-0 bg-transparent">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CHARGER_OPTIONS.map(opt => (
                                <SelectItem
                                  key={opt.id}
                                  value={opt.id}
                                  disabled={formData.charger_config.some((c, i) => i !== index && c.type === opt.id)}
                                >
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-[10px] text-slate-400 leading-tight">{info?.cost_range}</p>
                        </div>
                        <div className="flex items-center border border-slate-700 rounded overflow-hidden flex-shrink-0 bg-slate-800">
                          <button
                            type="button"
                            className="w-5 h-5 flex items-center justify-center text-slate-500 hover:bg-white/8 disabled:opacity-30 font-bold text-xs select-none"
                            onClick={() => updateChargerConfig(index, "quantity", Math.max(1, (config.quantity || 1) - 1))}
                            disabled={(config.quantity || 1) <= 1}
                          >−</button>
                          <span className="w-6 text-center font-mono font-semibold text-white text-xs select-none">
                            {config.quantity || 1}
                          </span>
                          <button
                            type="button"
                            className="w-5 h-5 flex items-center justify-center text-slate-500 hover:bg-white/8 disabled:opacity-30 font-bold text-xs select-none"
                            onClick={() => updateChargerConfig(index, "quantity", Math.min(20, (config.quantity || 1) + 1))}
                            disabled={(config.quantity || 1) >= 20}
                          >+</button>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="w-5 h-5 text-slate-300 hover:text-red-500 flex-shrink-0"
                          onClick={() => removeChargerConfig(index)}
                          disabled={formData.charger_config.length <= 1}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Solar + Budget */}
              <section className="space-y-3">
                <div className={`flex items-center gap-3 ${!solarFeasible ? "opacity-60" : ""}`}>
                  <Sun className={`w-4 h-4 flex-shrink-0 ${solarFeasible ? "text-amber-500" : "text-slate-400"}`} />
                  <div className="flex-1">
                    <Label className={`text-xs font-medium cursor-pointer ${solarFeasible ? "text-slate-300" : "text-slate-400"}`}>
                      Solar Canopy
                    </Label>
                    {!solarFeasible && (
                      <p className="text-[10px] text-amber-600 leading-tight">Requires site ≥ 10m × 10m</p>
                    )}
                  </div>
                  <Checkbox
                    checked={formData.solar_required && solarFeasible}
                    onCheckedChange={v => solarFeasible && handleInputChange("solar_required", v)}
                    disabled={!solarFeasible}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    Budget Range <span className="normal-case font-normal text-slate-400">(optional)</span>
                  </Label>
                  <Select value={formData.budget_range} onValueChange={v => handleInputChange("budget_range", v)}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select range" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="under_1cr">Under ₹1 Crore</SelectItem>
                      <SelectItem value="1_3cr">₹1–3 Crores</SelectItem>
                      <SelectItem value="3_5cr">₹3–5 Crores</SelectItem>
                      <SelectItem value="above_5cr">Above ₹5 Crores</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                    Land Lease / Rental <span className="normal-case font-normal text-slate-400">(optional)</span>
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="e.g., 50000"
                    className="h-8 text-sm"
                    value={formData.land_lease_cost_monthly}
                    onChange={e => handleInputChange("land_lease_cost_monthly", e.target.value)}
                  />
                  <p className="text-[10px] text-slate-400 leading-tight">Monthly ₹. Leave blank if you own the land.</p>
                </div>
              </section>
            </div>

            {/* Footer: save button */}
            <div className="px-5 py-4 border-t border-slate-700 flex-shrink-0 space-y-2">
              <Button
                onClick={handleSave}
                disabled={saving || !liveLayout}
                className="w-full bg-primary hover:bg-blue-600"
                data-testid="edit-sheet-save-btn"
              >
                {saving
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
                  : <><Lock className="w-3.5 h-3.5 mr-1.5" />Freeze &amp; Save</>
                }
              </Button>
              {!liveLayout && (
                <p className="text-xs text-amber-600 text-center">Enter land dimensions to unlock the canvas.</p>
              )}
            </div>
          </div>

          {/* ── Right: live canvas ── */}
          <div className="flex-1 min-w-0 overflow-auto bg-slate-900 p-2 flex items-start">
            <SiteLayoutMap
              key={canvasKey}
              layout={liveLayout}
              siteInputs={{ land_length: lengthInMeters, land_width: widthInMeters }}
              isRerunning={false}
              onRerun={null}
              onPositionsChange={pos => { frozenPositionsRef.current = pos; }}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
